'use client'

import { useState } from 'react'
import { MoreHorizontal, Edit, Trash2, Mail } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { CustomerAccount } from '../types'
import { useMutation } from '@apollo/client'
import {
  DELETE_ONE_CUSTOMER_ACCOUNT,
  FIND_MANY_CUSTOMER_ACCOUNT,
  CUSTOMER_ACCOUNT_SEND_INVITATION,
} from '../customerAccountQueries'
import { toast } from 'sonner'
import { useConfirmation } from '@/context/ConfirmationContext'

interface CustomerAccountActionsProps {
  customerAccount: CustomerAccount
  onEdit?: (customerAccount: CustomerAccount) => void
}

export function CustomerAccountActions({
  customerAccount,
  onEdit,
}: CustomerAccountActionsProps) {
  const { showConfirmation } = useConfirmation()

  const [deleteCustomerAccount, { loading: deleteLoading }] = useMutation(
    DELETE_ONE_CUSTOMER_ACCOUNT,
    {
      onError: e => {
        toast.error('Failed to delete customer account')
      },
      onCompleted: () => {
        toast.success('Customer account deleted successfully')
      },
      refetchQueries: [
        {
          query: FIND_MANY_CUSTOMER_ACCOUNT,
          variables: { customerId: customerAccount.customerId },
        },
      ],
    }
  )

  const [sendInvitation, { loading: inviteLoading }] = useMutation(
    CUSTOMER_ACCOUNT_SEND_INVITATION,
    {
      onError: e => {
        toast.error('Failed to send invitation')
      },
      onCompleted: () => {
        toast.success('Invitation sent successfully')
      },
    }
  )

  const handleEdit = () => {
    if (onEdit) {
      onEdit(customerAccount)
    }
  }

  const handleDelete = () => {
    showConfirmation({
      title: 'Delete Customer Account',
      description: `Are you sure you want to delete the account for ${customerAccount.email}? This action cannot be undone.`,
      onConfirm: () => {
        deleteCustomerAccount({
          variables: { id: customerAccount.id },
        })
      },
    })
  }

  const handleResendInvite = () => {
    sendInvitation({
      variables: { id: customerAccount.id },
    })
  }

  const showResendButton =
    customerAccount.status === 'INVITE_SENT' ||
    customerAccount.status === 'ACTIVE'

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-8 w-8 p-0">
          <span className="sr-only">Open menu</span>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={handleEdit}>
          <Edit className="mr-2 h-4 w-4" />
          Edit
        </DropdownMenuItem>
        {showResendButton && (
          <DropdownMenuItem
            onClick={handleResendInvite}
            disabled={inviteLoading}
          >
            <Mail className="mr-2 h-4 w-4" />
            Resend invite
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onClick={handleDelete}
          disabled={deleteLoading}
          className="text-red-600"
        >
          <Trash2 className="mr-2 h-4 w-4" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
